import type { ColumnDef } from '@tanstack/react-table';
import React from 'react';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDynamicLocale } from '@/hoslog-intl';
import { exportToCSV } from '@/lib/exportToCSV';
import { formatAmount } from '@/lib/format';
import { SalesBreakdownData } from '@/services/payment/payment.types';

export function getColumns(
  t: ReturnType<typeof useDynamicLocale>['t'],
  onExportRow?: (row: SalesBreakdownData, serialNumber: number) => void,
): ColumnDef<SalesBreakdownData>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'month',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.month')} />,
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.original.month}月</div>,
      meta: {
        label: t('common.page.tableColumn.month'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'numberOfCustomer',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.numberOfCustomers')} />
      ),
      cell: ({ row }) => <div>{row.getValue('numberOfCustomer')}</div>,
      meta: {
        label: t('common.page.tableColumn.numberOfCustomers'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'numberOfStore',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.numberOfStores')} />
      ),
      cell: ({ row }) => <div>{row.getValue('numberOfStore')}</div>,
      meta: {
        label: t('common.page.tableColumn.numberOfStores'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'numberOfAffiliate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.numberOfAffiliates')} />
      ),
      cell: ({ row }) => <div>{row.getValue('numberOfAffiliate')}</div>,
      meta: {
        label: t('common.page.tableColumn.numberOfAffiliates'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'receiveAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.receivedAmount')} />
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-right pr-3">{formatAmount(row.original.receiveAmount ?? 0)}</div>
      ),
      meta: {
        label: t('common.page.tableColumn.receivedAmount'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'salesBreakdown',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.salesBreakdown')} />
      ),
      cell: ({ row, table }) => {
        const serialNumber = getSerialNumber(table, row.index);
        const handleExportRow = (e: React.MouseEvent) => {
          e.preventDefault();
          if (onExportRow) {
            onExportRow(row.original, serialNumber);
            return;
          }

          const item = row.original;
          const monthLabel = item.monthName || (item.month ? `${item.month}月` : '');
          const exportRow = {
            [t('common.page.tableColumn.sn')]: serialNumber,
            [t('common.page.tableColumn.month')]: monthLabel,
            [t('common.page.tableColumn.numberOfCustomers')]: item.numberOfCustomer,
            [t('common.page.tableColumn.numberOfStores')]: item.numberOfStore,
            [t('common.page.tableColumn.numberOfAffiliates')]: item.numberOfAffiliate,
            [t('common.page.tableColumn.receivedAmount')]: item.receiveAmount,
          };

          exportToCSV([exportRow], {
            filename: monthLabel ? `sales-breakdown-${monthLabel}` : 'sales-breakdown',
            excludeColumns: ['select', 'actions'],
            onlySelected: false,
          });
        };

        return (
          <button
            type="button"
            onClick={handleExportRow}
            className="text-link underline underline-offset-2 cursor-pointer hover:opacity-80 transition-opacity bg-transparent p-0 border-none font-medium"
          >
            {t('common.page.table.csv')}
          </button>
        );
      },
      meta: {
        label: t('common.page.tableColumn.salesBreakdown'),
      },
      enableHiding: false,
      enableSorting: false,
    },
  ];
}
